import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {HttpClient, HttpEventType} from "@angular/common/http";
import {ApiService} from "../services/api.service";
import {UploadsStatusComponent} from "./uploads-status/uploads-status.component";
import {UntypedFormControl, FormGroupDirective, NgForm, Validators} from "@angular/forms";
import {ErrorStateMatcher} from "@angular/material/core";
import {ObjectStatus} from "../object-status";
import {MatDialog} from "@angular/material/dialog";
import {ShareFilesComponent} from "./share-files/share-files.component";

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.css']
})
export class UploadComponent implements OnInit {

  ngOnInit() {

  }

  @Input()
  requiredFileType: string | null = null;

  @ViewChild(UploadsStatusComponent)
  uploadsStatusComponent!: UploadsStatusComponent;
  /** Child component where is displayed uploaded files */
  isShareDisabled = true;
  /** Boolean flag, disabled if no files are selected */

  constructor(public dialog: MatDialog, private http: HttpClient, private apiService: ApiService) {
  }

  openShareDialog() {
    const dialogRef = this.dialog.open(ShareFilesComponent, {
      data: this.uploadsStatusComponent.shareFileList,
      width: '600px',
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe(result => {
      this.uploadsStatusComponent.loadStatus();
      this.uploadsStatusComponent.shareFileList = [];
      this.isShareDisabled = true;
    });
  }

  onFileSelected(event: any) {
    const files = <File[]>Object.values(event.target.files)
    files.forEach((file: File) => this.upload(file))
  }

  onFileDropped(event: any) {
    const files = <File[]>Object.values(event)
    files.forEach((file: File) => this.upload(file))
  }

  upload(file: File) {
    const MAX_FILE_SIZE = 50 * 1024 * 1024 * 1024; // 50 GB
    const MULTIPART_THRESHOLD = 4 * 1024 * 1024 * 1024; // 4 GB

    if (file.size > MAX_FILE_SIZE) {
      alert(`File size exceeds maximum allowed size of 50 GB. File: ${file.name}, Size: ${(file.size / (1024 * 1024 * 1024)).toFixed(2)} GB`);
      return;
    }

    // Create temporary upload entry immediately to show progress bar at 0%
    const tempKey = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileObjectStatus: ObjectStatus = <ObjectStatus>{
      object_key: tempKey,
      file_name: file.name,
      file_size: file.size,
      upload_state: "IN PROGRESS",
      upload_date: new Date().toString()
    };
    this.uploadsStatusComponent.addToUploadList(fileObjectStatus);
    
    // Initialize progress tracking immediately at 0%
    this.uploadsStatusComponent.localObjectStateMap[tempKey] = {
      progress: 0,
      uploadedBytes: 0,
      totalBytes: file.size
    };

    if (file.size > MULTIPART_THRESHOLD) {
      this.uploadMultipart(file, tempKey);
    } else {
      this.uploadSinglePart(file, tempKey);
    }
  }

  uploadSinglePart(file: File, tempKey: string) {
    if (file) {
      this.apiService.getUploadUrl().subscribe(
        (getUploadUrlResponse: any) => {
          const objectKey = getUploadUrlResponse.object_key;
          
          // Update temp entry with real object key
          const uploadEntry = this.uploadsStatusComponent.uploadList.find(f => f.object_key === tempKey);
          if (uploadEntry) {
            uploadEntry.object_key = objectKey;
          }
          
          // Move progress tracking from temp key to real key
          this.uploadsStatusComponent.localObjectStateMap[objectKey] = this.uploadsStatusComponent.localObjectStateMap[tempKey];
          delete this.uploadsStatusComponent.localObjectStateMap[tempKey];
          
          const upload$ = this.http.put(getUploadUrlResponse.upload_url,
            file,
            {
              reportProgress: true,
              observe: 'events'
            })
          
          this.uploadsStatusComponent.localObjectStateMap[objectKey].subscription = upload$.subscribe(
            (event: any) => {
              if (event.type == HttpEventType.UploadProgress) {
                this.uploadsStatusComponent.localObjectStateMap[objectKey].progress = Math.round(100 * (event.loaded / event.total));
                this.uploadsStatusComponent.localObjectStateMap[objectKey].uploadedBytes = event.loaded;
                this.uploadsStatusComponent.localObjectStateMap[objectKey].totalBytes = event.total;
              } else if (event.type == HttpEventType.Response) {
                this.apiService.postFile({
                  file_name: file.name,
                  file_size: file.size,
                  object_key: objectKey
                }).subscribe((postFileResponse: any) => {
                  this.uploadsStatusComponent.loadStatus();
                })
              }
            },
            (error: any) => {
              console.error('Upload error:', error);
              alert(`Upload failed for ${file.name}. Please try again.`);
              // Remove the failed upload from the list
              this.uploadsStatusComponent.removeFromUploadList(objectKey);
            }
          )
        },
        (error: any) => {
          console.error('Failed to get upload URL:', error);
          alert(`Failed to initiate upload for ${file.name}. Please try again.`);
          // Remove the temp entry on error
          this.uploadsStatusComponent.removeFromUploadList(tempKey);
        }
      )
    }
  }

  uploadMultipart(file: File, tempKey: string) {
    // Initiate multipart upload and get all presigned URLs
    // Part size is calculated automatically by the server based on file size
    this.apiService.initiateMultipartUpload(file.name, file.size).subscribe(
      (response: any) => {
        if (!response) {
          alert('Failed to initiate multipart upload');
          this.uploadsStatusComponent.removeFromUploadList(tempKey);
          return;
        }

        const { object_key, upload_id, part_urls, part_size } = response;

        // Update temp entry with real object key
        const uploadEntry = this.uploadsStatusComponent.uploadList.find(f => f.object_key === tempKey);
        if (uploadEntry) {
          uploadEntry.object_key = object_key;
        }
        
        // Move progress tracking from temp key to real key
        this.uploadsStatusComponent.localObjectStateMap[object_key] = this.uploadsStatusComponent.localObjectStateMap[tempKey];
        delete this.uploadsStatusComponent.localObjectStateMap[tempKey];
        
        // Store upload_id and subscriptions for cancellation
        this.uploadsStatusComponent.localObjectStateMap[object_key].upload_id = upload_id;
        this.uploadsStatusComponent.localObjectStateMap[object_key].subscriptions = [];

        const uploadedParts: any[] = [];
        let completedParts = 0;
        let hasError = false;
        const partBytesUploaded: { [key: number]: number } = {};

        // Initialize all parts to 0
        part_urls.forEach((partInfo: any) => {
          partBytesUploaded[partInfo.partNumber] = 0;
        });

        // Upload each part directly to S3 using presigned URLs
        part_urls.forEach((partInfo: any, index: number) => {
          const start = index * part_size;
          const end = Math.min(start + part_size, file.size);
          const blob = file.slice(start, end);

          // Direct upload to S3
          const partSubscription = this.http.put(partInfo.url, blob, {
            reportProgress: true,
            observe: 'events'
          }).subscribe(
            (event: any) => {
              if (hasError) return; // Skip processing if an error has occurred
              
              if (event.type === HttpEventType.UploadProgress) {
                // Update bytes for this specific part
                partBytesUploaded[partInfo.partNumber] = event.loaded;
                
                // Calculate total bytes uploaded across all parts
                const totalBytesUploaded = Object.values(partBytesUploaded).reduce((sum, bytes) => sum + bytes, 0);
                
                // Calculate overall progress percentage
                const overallProgress = Math.min(100, Math.round((totalBytesUploaded / file.size) * 100));
                
                // Update progress atomically to prevent flickering
                this.uploadsStatusComponent.localObjectStateMap[object_key].progress = overallProgress;
                this.uploadsStatusComponent.localObjectStateMap[object_key].uploadedBytes = totalBytesUploaded;
                this.uploadsStatusComponent.localObjectStateMap[object_key].totalBytes = file.size;
              } else if (event.type === HttpEventType.Response) {
                // Get ETag from response headers
                const etag = event.headers.get('ETag')?.replace(/"/g, '');
                uploadedParts.push({
                  PartNumber: partInfo.partNumber,
                  ETag: etag
                });
                completedParts++;

                // Complete multipart upload when all parts are done
                if (completedParts === part_urls.length && !hasError) {
                  uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);
                  this.apiService.completeMultipartUpload(object_key, upload_id, uploadedParts).subscribe(
                    (completeResponse: any) => {
                      if (completeResponse) {
                        this.apiService.postFile({
                          file_name: file.name,
                          file_size: file.size,
                          object_key: object_key
                        }).subscribe(() => {
                          this.uploadsStatusComponent.loadStatus();
                        });
                      } else {
                        alert('Failed to complete multipart upload');
                        this.uploadsStatusComponent.removeFromUploadList(object_key);
                      }
                    },
                    (error: any) => {
                      console.error('Error completing multipart upload:', error);
                      alert('Failed to complete multipart upload');
                      this.uploadsStatusComponent.removeFromUploadList(object_key);
                    }
                  );
                }
              }
            },
            (error: any) => {
              if (hasError) return; // Prevent multiple alerts
              hasError = true;
              console.error(`Error uploading part ${partInfo.partNumber}:`, error);
              alert(`Error uploading part ${partInfo.partNumber}. Upload failed for ${file.name}.`);
              // Remove the failed upload from the list
              this.uploadsStatusComponent.removeFromUploadList(object_key);
            }
          );
          
          // Store subscription for cancellation
          this.uploadsStatusComponent.localObjectStateMap[object_key].subscriptions.push(partSubscription);
        });
      },
      (error: any) => {
        console.error('Failed to initiate multipart upload:', error);
        alert(`Failed to initiate multipart upload for ${file.name}. Please try again.`);
        // Remove the temp entry on error
        this.uploadsStatusComponent.removeFromUploadList(tempKey);
      }
    );
  }

  toggleShare(shareFileList: string[]) {
    this.isShareDisabled = shareFileList.length == 0;
  }
}
